import { useEffect, useState } from 'react';
import { AlertTriangle, AlertOctagon, AlertCircle, Loader2, Upload, Info, CircleCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { AdminAPI } from '@/lib/admin-api-quality';
import { cn } from '@/lib/utils';
import { ConflictPanel } from '@/components/admin/ConflictPanel';
import { ParityPanel } from '@/components/admin/ParityPanel';

export const PrePublishDialog = ({ open, onOpenChange, cardId, versionId, onConfirm, publishing }) => {
  const [conflicts, setConflicts] = useState(null);
  const [loadingConflicts, setLoadingConflicts] = useState(true);
  const [parity, setParity] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => {
    if (!open || !versionId) return;
    setLoadingConflicts(true);
    AdminAPI.quality.conflicts(versionId)
      .then(setConflicts)
      .catch(() => setConflicts({ counts: {}, findings: [] }))
      .finally(() => setLoadingConflicts(false));
  }, [open, versionId]);

  const counts = conflicts?.counts || {};
  const hasCritical = (counts.critical || 0) > 0;
  const hasHigh = (counts.high || 0) > 0;
  const hasMedium = (counts.medium || 0) > 0;
  const parityCounts = parity?.summary?.counts || {};
  const hasParityWarn = (parityCounts.warn || 0) + (parityCounts.critical || 0) > 0;
  const overrideRequired = hasCritical || hasHigh || hasParityWarn;
  const canSubmit = !overrideRequired || overrideReason.trim().length >= 8;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="admin-pre-publish-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Publish rule version</DialogTitle>
          <DialogDescription className="text-xs">
            Verify conflicts and parity diffs before activating this draft. Override is required if there are critical/high findings or parity anomalies.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-4">
          {/* Conflict summary */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold">1 · Conflict review</div>
            </div>
            {loadingConflicts ? (
              <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Detecting conflicts…</div>
            ) : (
              <ConflictPanel counts={conflicts?.counts} findings={conflicts?.findings || []} compact />
            )}
          </section>

          {/* Parity */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold">2 · Parity battery</div>
              <span className="text-[10px] text-muted-foreground">deterministic battery vs current active version</span>
            </div>
            <ParityPanel cardId={cardId} versionId={versionId} onComplete={setParity} />
          </section>

          {/* Gating message */}
          {overrideRequired && (
            <Card className="p-3 border-amber-500/40 bg-amber-500/5">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-semibold text-amber-700 dark:text-amber-300">Override reason required</div>
                  <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-1">
                    {hasCritical && 'Critical conflicts detected. '}
                    {hasHigh && 'High-severity conflicts detected. '}
                    {hasParityWarn && 'Parity battery deviations detected. '}
                    Indian rewards behaviour is messy and experimental routes are valid — we don’t block publish, but we require a reason on record.
                  </p>
                  <Label className="text-[10px] uppercase tracking-wider mt-3 inline-block">Override reason (min 8 chars)</Label>
                  <Textarea
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    rows={2}
                    className="mt-1 text-xs"
                    placeholder="e.g. C2C route is experimental and intentionally accepted; ops verified with support on 25 May…"
                    data-testid="admin-pre-publish-override-reason"
                  />
                </div>
              </div>
            </Card>
          )}

          {!loadingConflicts && !overrideRequired && (
            <Card className="p-3 border-emerald-500/40 bg-emerald-500/5 flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
              <CircleCheck className="h-4 w-4 shrink-0" /> No blocking findings. Safe to publish.
            </Card>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={publishing}>Cancel</Button>
          <Button onClick={() => onConfirm(overrideReason || null)} disabled={!canSubmit || publishing} className="gap-1.5" data-testid="admin-pre-publish-confirm">
            {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {publishing ? 'Publishing…' : (overrideRequired ? 'Publish with override' : 'Publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
